import * as PIXI from "pixi.js";
import FXManager from "../../Engine/Effects/FXManager";
import { EASING_FNS, evaluatePath } from "../../Engine/Collection/BezierPath";
import { FEATURES_CONFIG } from "../../featuresConfig";
import { soundManager } from "../../SoundManager";
import { SOUND_NAME } from "../../sounds";

/**
 * coinToLogoTrail — a landed coin throws a fire trail into the Pot.
 *
 * Cleopatra's Fire GDD §5.1: every coin that lands sends a trail into the fire
 * cup on the logo, BEFORE any line win shows.
 *
 * The trail is Zeus's `trail_coin` (effects/trail/particle_trail.json —
 * soft_glow, additive, dense), flown the way Zeus's value traveler flew it: a
 * point moves along a Bézier, the emitter follows it. Tuned for fire
 * (FEATURES_CONFIG.LOGO_POT):
 *
 *   size    the biggest particle is TRAIL_SIZE × the symbol height, worked out
 *           per trail from the live size — so it follows every resize. The
 *           particle scale ramp and the spawn spread are scaled together, so
 *           the trail gets wider as well as bigger. NOT through emitter.init's
 *           scaleMod: RevoltFX multiplies particle LIFE by it too, which would
 *           stretch the tail with the screen. One cached settings clone per
 *           screen size (the factor is rounded).
 *   tint    TRAIL_TINT_START → TRAIL_TINT_END, a hot head burning out to deep
 *           orange-red.
 *   budget  TRAIL_SPAWN_COUNT particles per spawn, at most TRAIL_SPAWN_RATE spawns a
 *           second — whatever the screen's refresh rate. As authored,
 *           trail_coin spawned 30 EVERY frame (its 0.01 s gap is shorter than
 *           a frame), so a 120 Hz screen drew twice the particles of a 60 Hz
 *           one. The glows are big and overlap, so far fewer read the same.
 *   arc     ONE even arc, like a throw: a quadratic whose peak is halfway
 *           across, TRAIL_ARC_HEIGHT × the horizontal distance (+
 *           TRAIL_ARC_LIFT symbol heights) above the higher end. Scaled with
 *           the distance, so every trail has the same shape and comes DOWN
 *           into the bowl at the same angle, near or far. The arc's top is
 *           held on screen (a quadratic peaks halfway to its control).
 *           The target: the logo's centre across, at its fire bone's height
 *           (getLogoTarget); TRAIL_TARGET_OFFSET_Y fine-tunes it.
 *
 * ── NON-BLOCKING, never cancelled ──
 *
 * Nothing in the game waits for a trail — not the next spin, not the line
 * wins, at any speed (Vladimir, 2026-09-28: "non blocking feature like
 * lightning"). A trail that was shot flies to the logo and lands its hit
 * whatever happens to the spin that shot it. Only a destroyed layer (the room
 * unmounting) ends one early.
 */

// const EMITTER = "trail_coin";
// The flame trail's own emitter (effects/flame_trail/flame_trail.json).
const EMITTER = "flame_trail";
// trail_coin as authored (particle_trail.json): soft_glow.png is 200 px, the
// scale ramps 0.1–0.2 → 0.3–0.5, spawn radius 7.
// ── Its particle is FIRE now (2026-10-06) ── `fire_wisp.png`, 200 px like
// soft_glow (so every size here holds): the coin's own fireball frame
// (c.webp coin_00000, un-premultiplied) under a soft radial falloff, packed
// next to soft_glow in particle_trail.png. Back to the old look: set the
// emitter's componentId to "soft_glow.png" again.
// ── ...and now the FIRE FRAME itself (2026-10-06) ── the `fire_wisp.png`
// slot holds logo_fire frame 12 (the same art as basic_win's fire), its fire
// mass centred and its flat base softened, and the emitter starts every
// particle at a random rotation (randomStartRotation): dozens of rotated
// flames make the head a churning fireball, yellow-white in the middle,
// orange, then red down the tail. Additive, as authored.
const NATIVE = {
  PARTICLE_MAX_PX: 200 * 0.5,
  SCALE_START: [0.1, 0.2],
  SCALE_END: [0.3, 0.5],
  SPAWN_RADIUS: 7,
};

// ── The flame trail's particle is 128 px (2026-10-06, lighter: was 200) ──
// Every size above is in the 200-px units the trail was tuned in, so the
// particle's scales are multiplied by this — the same size on screen.
const PARTICLE_TEXTURE_FIX = 200 / 128;

const _point = new PIXI.Point();

const cfg = () => FEATURES_CONFIG.LOGO_POT || {};

// Portrait (mobile) gets its own throw — see the arc in fireCoinToLogo.
const isPortrait = () =>
  typeof window !== "undefined" && window.innerHeight > window.innerWidth;



/**
 * @param {object} p
 * @param {{trailContainer: PIXI.Container, symbolHeight: number}} p.layer
 *        the room's trail layer: drawn into, and its live symbol height sizes
 *        the fire. Base game: its trail animator (LevelChargingAnimator, which
 *        carries both). Free spins: freeSpinsCoinTrailRef.
 * @param {PIXI.DisplayObject} p.from   the coin's overlay cell (centre)
 * @param {{x:number,y:number}|null} p.target  the logo's centre, GLOBAL space
 * @param {Function} [p.onArrival]      the logo's hit
 * @param {PIXI.DisplayObject} [p.carry] rides the trail's head the whole way
 *        (the H&W End Collector's flying coin value) — the same path, the same
 *        speed as the trail itself. Destroyed when the trail finishes.
 * @param {Function} [p.onProgress]  called every frame with the head's eased
 *        progress, 0 -> 1 (the flying value grows with it)
 */
export const fireCoinToLogo = ({
  layer,
  from,
  target,
  onArrival,
  carry = null,
  onProgress = null,
  // The LIGHT profile (LOGO_POT.TRAIL_LIGHT_*): far fewer particles, for
  // trails flown as a burst (a skipped H&W collection).
  light = false,
  // ── The portrait swing on EVERY orientation (Hold & Win, 2026-10-06) ──
  // A (nearly) vertical throw swings out to one side instead of flying a
  // straight line — the swing the base game's portrait throw has. Off for the
  // logo trails, which keep their own rules.
  bowVertical = false,
  // ── Its own, gentler curve (Hold & Win, 2026-10-06: "too exaggerated …
  // next to each other there is no need for this much curving") ── when
  // given: { height, lift, sideBow } — the arc's height × the horizontal
  // distance (+ lift symbol heights), and the vertical swing × the VERTICAL
  // distance, so neighbours bend a little and far cells more. The logo's
  // portrait apex is not used with it.
  arc = null,
  // ── A trail's own look (2026-10-15, the scarab's shot) ── all optional:
  //   flat           a straight line, no arc / bow / swing
  //   durationMs     its flight (default LOGO_POT.TRAIL_DURATION_MS)
  //   spawnCount     particles per spawn, particleLife their life (s)
  //   flameSpawnMs   the gap between tail flames, flameLifeMs their life
  //   tintStart / tintEnd  the particles' colour, head -> tail
  //   flameTint      the tail flames' tint
  //   sizeScale      the fireball's width (particles, flames, spread) x this
  //   bowLeft        with `arc`: the bow always on the LEFT of the direction
  //                  of travel (the scarab's shot)
  style = null,
  // ── Its sound (2026-10-22) ── the Hold & Win collections fly on their own
  // quieter copy (hold_and_win_trail), under the chest's collect.
  sound = SOUND_NAME.trail_sound,
}) => {
  const container = layer?.trailContainer;
  if (!container || container.destroyed || !from || from.destroyed || !target) return;
  const c = cfg();

  _point.set(0, 0);
  const startG = from.toGlobal(_point);
  const start = container.toLocal(startG);
  const startPos = { x: start.x, y: start.y };
  // The symbol height in THIS container's units: a layer inside the scene
  // spine is scaled with the scene, so the screen size is divided back out.
  const wt = container.worldTransform;
  const worldScale = Math.hypot(wt.a, wt.b) || 1;
  const symbolHeight = (layer.symbolHeight || 0) / worldScale;
  _point.set(target.x, target.y);
  const end = container.toLocal(_point);
  // Fine-tune up from the fire bone (0 = the fire itself).
  const endPos = {
    x: end.x,
    y: end.y - (c.TRAIL_TARGET_OFFSET_Y ?? 0) * symbolHeight,
  };
  // One even arc: peak halfway across, its height scaled with the distance
  // so near and far trails share one shape and one landing angle.
  // const lift =
  //   (c.TRAIL_ARC_HEIGHT ?? 0.5) * Math.abs(startPos.x - endPos.x) +
  //   (c.TRAIL_ARC_LIFT ?? 0) * symbolHeight;
  const lift =
    (arc?.height ?? c.TRAIL_ARC_HEIGHT ?? 0.5) * Math.abs(startPos.x - endPos.x) +
    (arc?.lift ?? c.TRAIL_ARC_LIFT ?? 0) * symbolHeight;
  let controlY = Math.min(startPos.y, endPos.y) - lift;
  // The arc's TOP stays on screen — the logo sits near the top in landscape.
  // A quadratic's highest point is (s·e − c²) / (s − 2c + e), which is ABOVE
  // its halfway point whenever the ends differ in height; keeping it at or
  // below the line T needs c ≥ T − √((s − T)(e − T)).
  _point.set(0, 0);
  const screenTop =
    container.toLocal(_point).y + (c.TRAIL_TOP_MARGIN ?? 0.15) * symbolHeight;
  const below = (startPos.y - screenTop) * (endPos.y - screenTop);
  const minControlY = below > 0 ? screenTop - Math.sqrt(below) : screenTop;
  let controlX = (startPos.x + endPos.x) / 2;

  // ── PORTRAIT: a basketball throw (2026-10-01) ── landscape and desktop
  // keep the arc above, untouched. The arc's TOP is put APEX symbol heights
  // above the logo, so it rises past the fire and drops into it from above.
  // A quadratic's top is P = (s·e − c²)/(s − 2c + e); solved for the control:
  // c = P − √((s − P)(e − P)) (both ends below P).
  // if (isPortrait()) {
  if (isPortrait() || bowVertical) {
    // The apex lift is the PORTRAIT logo throw's own (bowVertical alone
    // keeps the arc above and only adds the swing below).
    // if (isPortrait()) {
    if (isPortrait() && !arc) {
      const peak = Math.min(startPos.y, endPos.y) - (c.TRAIL_PORTRAIT_APEX ?? 0.6) * symbolHeight;
      const under = (startPos.y - peak) * (endPos.y - peak);
      if (under > 0) controlY = peak - Math.sqrt(under);
    }
    // A (nearly) vertical throw — the middle reel — swings out to one side.
    const dx = endPos.x - startPos.x;
    const range = (c.TRAIL_PORTRAIT_SIDE_RANGE ?? 1) * symbolHeight;
    const fade = range > 0 ? Math.max(0, 1 - Math.abs(dx) / range) : 0;
    if (fade > 0) {
      // Outward: away from the logo's side; dead centre picks at random.
      const side = Math.abs(dx) > 1 ? -Math.sign(dx) : Math.random() < 0.5 ? -1 : 1;
      // controlX += side * fade * (c.TRAIL_PORTRAIT_SIDE_BOW ?? 1.6) * symbolHeight;
      controlX +=
        side *
        fade *
        (arc
          ? (arc.sideBow ?? 0.5) * Math.abs(endPos.y - startPos.y)
          : (c.TRAIL_PORTRAIT_SIDE_BOW ?? 1.6) * symbolHeight);
    }
  }

  // ── Hold & Win: ONE curve for every direction (2026-10-06) ── with `arc`,
  // the control sits off the MIDDLE of the line between the two cells, on
  // its perpendicular, `height` x the distance (+ `lift` symbol heights) —
  // the slight bow a horizontal trail already had, now the same for a
  // vertical or diagonal one (a vertical one rose past its top cell and
  // hooked back). It bows upward; a (near) vertical one to a consistent side
  // — away from the target's side, left when straight.
  if (arc) {
    const vx = endPos.x - startPos.x;
    const vy = endPos.y - startPos.y;
    const dist = Math.hypot(vx, vy) || 1;
    let nx = -vy / dist;
    let ny = vx / dist;
    if (style?.bowLeft) {
      // The left of the direction of travel (screen y runs down).
      nx = vy / dist;
      ny = -vx / dist;
    } else {
      if (ny > 0) {
        nx = -nx;
        ny = -ny;
      }
      if (Math.abs(ny) < 0.2) {
        const side = Math.abs(vx) > 1 ? -Math.sign(vx) : -1;
        nx = Math.abs(nx) * side;
      }
    }
    const offset = (arc.height ?? 0.3) * dist + (arc.lift ?? 0) * symbolHeight;
    controlX = (startPos.x + endPos.x) / 2 + nx * offset;
    controlY = (startPos.y + endPos.y) / 2 + ny * offset;
  }

  controlY = Math.max(controlY, minControlY);
  // Flat: the control on the line itself — a straight shot.
  if (style?.flat) {
    controlX = (startPos.x + endPos.x) / 2;
    controlY = (startPos.y + endPos.y) / 2;
  }
  const path = {
    start: startPos,
    // control: { x: (startPos.x + endPos.x) / 2, y: controlY },
    control: { x: controlX, y: controlY },
    end: endPos,
  };

  const wrapper = new PIXI.Container();
  wrapper.name = "coin_to_logo_trail";
  wrapper.position.set(startPos.x, startPos.y);
  wrapper.zIndex = 200;
  container.addChild(wrapper);
  // // ── The head flame (LOGO_POT.TRAIL_FLAME) ── under the carried value.
  // const flameCfg = c.TRAIL_FLAME;
  // const flameFrames =
  //   flameCfg?.ENABLED && PIXI.Assets.cache.has("flame_trail")
  //     ? PIXI.Assets.get("flame_trail")?.animations?.flame
  //     : null;
  // let flame = null;
  // if (flameFrames?.length) {
  //   flame = new PIXI.AnimatedSprite(flameFrames);
  //   flame.anchor.set(0.5, flameCfg.ANCHOR_Y ?? 0.88);
  //   flame.blendMode = PIXI.BLEND_MODES.ADD;
  //   flame.alpha = flameCfg.ALPHA ?? 1;
  //   flame.scale.set(((flameCfg.SIZE ?? 0.75) * symbolHeight) / (flameFrames[0].height || 200));
  //   flame.animationSpeed = flameCfg.SPEED ?? 0.5;
  //   flame.gotoAndPlay(Math.floor(Math.random() * flameFrames.length));
  //   wrapper.addChild(flame);
  // }
  // ── The FLAME TAIL (LOGO_POT.TRAIL_FLAME) ── the ball drops a flame behind
  // it every SPAWN_MS; each one burns in place and dies on its own, so the
  // tail outlives the flight by up to LIFE_MS.
  const flameCfg = c.TRAIL_FLAME;
  // style.fire === false: no fire at all — only `carry` flies (the scarab's
  // letters, 2026-10-22).
  const fireOff = style?.fire === false;
  const flameFrames =
    !fireOff && flameCfg?.ENABLED && PIXI.Assets.cache.has("flame_trail")
      ? PIXI.Assets.get("flame_trail")?.animations?.flame
      : null;
  const flames = [];
  // const flameGap = light ? flameCfg?.LIGHT_SPAWN_MS ?? 70 : flameCfg?.SPAWN_MS ?? 28;
  // const flameLife = flameCfg?.LIFE_MS ?? 260;
  const flameGap =
    style?.flameSpawnMs ??
    (light ? flameCfg?.LIGHT_SPAWN_MS ?? 70 : flameCfg?.SPAWN_MS ?? 28);
  const flameLife = style?.flameLifeMs ?? flameCfg?.LIFE_MS ?? 260;
  const sizeScale = style?.sizeScale ?? 1;
  const flameScale0 = flameFrames?.length
    ? ((flameCfg.SIZE ?? 0.55) * symbolHeight * sizeScale) / (flameFrames[0].height || 200)
    : 0;
  let sinceFlame = flameGap;
  let flying = true;
  const dropFlame = (x, y, heading) => {
    const f = new PIXI.AnimatedSprite(flameFrames);
    f.anchor.set(0.5, flameCfg.ANCHOR_Y ?? 0.88);
    f.blendMode = PIXI.BLEND_MODES.ADD;
    if (style?.flameTint != null) f.tint = style.flameTint;
    f.position.set(x, y);
    // Its frames burn upward: rotation = heading - 90° points it BACK.
    f.rotation = heading - Math.PI / 2;
    f.scale.set(flameScale0);
    f.alpha = flameCfg.ALPHA ?? 0.95;
    f.animationSpeed = flameCfg.SPEED ?? 0.6;
    f.gotoAndPlay(Math.floor(Math.random() * flameFrames.length));
    f._age = 0;
    // Under the ball (the wrapper sits at zIndex 200).
    f.zIndex = 199;
    container.addChild(f);
    flames.push(f);
  };
  const burn = () => {
    const dt = PIXI.Ticker.shared.deltaMS;
    for (let i = flames.length - 1; i >= 0; i--) {
      const f = flames[i];
      if (f.destroyed || container.destroyed) {
        flames.splice(i, 1);
        continue;
      }
      f._age += dt;
      const k = Math.min(1, f._age / flameLife);
      f.scale.set(flameScale0 * (1 - (1 - (flameCfg.END_SCALE ?? 0.35)) * k));
      f.alpha = (flameCfg.ALPHA ?? 0.95) * (1 - k);
      if (k >= 1) {
        f.stop();
        f.destroy();
        flames.splice(i, 1);
      }
    }
    if (!flying && !flames.length) PIXI.Ticker.shared.remove(burn);
  };
  if (flameFrames?.length) PIXI.Ticker.shared.add(burn);

  if (carry) wrapper.addChild(carry);

  // Rounded, so a resize makes one new settings clone, not one per trail.
  // const k =
  //   Math.round((((c.TRAIL_SIZE ?? 0.3) * symbolHeight) / NATIVE.PARTICLE_MAX_PX) * 100) /
  //     100 || 1;
  const k =
    Math.round(
      (((c.TRAIL_SIZE ?? 0.3) * symbolHeight * sizeScale) / NATIVE.PARTICLE_MAX_PX) * 100,
    ) / 100 || 1;
  // The gap between spawns. RevoltFX times it with Date.now() in whole ms, so
  // a 60 Hz frame reads 16 or 17 ms — a gap of exactly 1/60 s (16.67) would
  // skip every 16 ms frame and halve the trail. 10% slack: every 60 Hz frame
  // still spawns, a 120 Hz screen spawns every other frame.
  const spawnGap = 0.9 / (c.TRAIL_SPAWN_RATE ?? 60);
  // const spawnCount = light
  //   ? c.TRAIL_LIGHT_SPAWN_COUNT ?? 6
  //   : c.TRAIL_SPAWN_COUNT ?? 10;
  const spawnCount =
    style?.spawnCount ??
    (light ? c.TRAIL_LIGHT_SPAWN_COUNT ?? 6 : c.TRAIL_SPAWN_COUNT ?? 10);
  const emitter = fireOff ? null : FXManager.emitTrail(container, wrapper, {
    emitterName: EMITTER,
    // particleLife: c.TRAIL_PARTICLE_LIFE ?? 0.2,
    // particleLife: light
    //   ? c.TRAIL_LIGHT_PARTICLE_LIFE ?? 0.2
    //   : c.TRAIL_PARTICLE_LIFE ?? 0.2,
    particleLife:
      style?.particleLife ??
      (light ? c.TRAIL_LIGHT_PARTICLE_LIFE ?? 0.2 : c.TRAIL_PARTICLE_LIFE ?? 0.2),
    spawnRadius: NATIVE.SPAWN_RADIUS * k,
    emitterOverrides: {
      // spawnCountMin: c.TRAIL_SPAWN_COUNT ?? 10,
      // spawnCountMax: c.TRAIL_SPAWN_COUNT ?? 10,
      spawnCountMin: spawnCount,
      spawnCountMax: spawnCount,
      spawnFrequencyMin: spawnGap,
      spawnFrequencyMax: spawnGap,
    },
    // The fireball's shape (LOGO_POT.TRAIL_HEAD_SCALE / _TAIL_SCALE): born
    // big at the head, shrinking behind it — a ball-like tip and a pointed
    // tail. Was NATIVE.SCALE_START -> SCALE_END (growing: fat at the tail).
    particleOverrides: {
      // scaleStartMin: (c.TRAIL_HEAD_SCALE?.[0] ?? NATIVE.SCALE_END[0]) * k,
      // scaleStartMax: (c.TRAIL_HEAD_SCALE?.[1] ?? NATIVE.SCALE_END[1]) * k,
      // scaleEndMin: (c.TRAIL_TAIL_SCALE?.[0] ?? NATIVE.SCALE_START[0]) * k,
      // scaleEndMax: (c.TRAIL_TAIL_SCALE?.[1] ?? NATIVE.SCALE_START[1]) * k,
      scaleStartMin: (c.TRAIL_HEAD_SCALE?.[0] ?? NATIVE.SCALE_END[0]) * k * PARTICLE_TEXTURE_FIX,
      scaleStartMax: (c.TRAIL_HEAD_SCALE?.[1] ?? NATIVE.SCALE_END[1]) * k * PARTICLE_TEXTURE_FIX,
      scaleEndMin: (c.TRAIL_TAIL_SCALE?.[0] ?? NATIVE.SCALE_START[0]) * k * PARTICLE_TEXTURE_FIX,
      scaleEndMax: (c.TRAIL_TAIL_SCALE?.[1] ?? NATIVE.SCALE_START[1]) * k * PARTICLE_TEXTURE_FIX,
      // tintStart: c.TRAIL_TINT_START ?? 0xfff1a8,
      // tintEnd: c.TRAIL_TINT_END ?? 0xff3c00,
      tintStart: style?.tintStart ?? c.TRAIL_TINT_START ?? 0xfff1a8,
      tintEnd: style?.tintEnd ?? c.TRAIL_TINT_END ?? 0xff3c00,
    },
  });
  // soundManager.playOncePerFrame(SOUND_NAME.trail_sound);
  soundManager.playOncePerFrame(sound);

  // const duration = c.TRAIL_DURATION_MS ?? 350;
  const duration = style?.durationMs ?? c.TRAIL_DURATION_MS ?? 350;
  const ease = EASING_FNS.easeInOut;
  let elapsed = 0;

  const finish = (arrived) => {
    PIXI.Ticker.shared.remove(tick);
    if (emitter) {
      emitter.target = null;
      FXManager.stopTrail(emitter); // the particles already out burn off
    }
    if (carry && !carry.destroyed) {
      carry.parent?.removeChild(carry);
      carry.destroy();
    }
    // if (flame && !flame.destroyed) {
    //   flame.stop();
    //   flame.destroy();
    // }
    // The tail's flames burn out on their own (burn); none are dropped now.
    flying = false;
    if (!wrapper.destroyed) wrapper.destroy();
    if (arrived) onArrival?.();
  };
  const tick = () => {
    if (container.destroyed || wrapper.destroyed) {
      finish(false);
      return;
    }
    elapsed += PIXI.Ticker.shared.deltaMS;
    const t = Math.min(1, elapsed / duration);
    const progress = ease(t);
    const pos = evaluatePath(path, progress);
    // // The flame points BACK along the flight (its frames burn upward, so
    // // rotation = heading - 90°), turning smoothly; it fades in the last 15%.
    // if (flame && !flame.destroyed) {
    //   const hx = pos.x - wrapper.x;
    //   const hy = pos.y - wrapper.y;
    //   if (hx * hx + hy * hy > 0.01) {
    //     const target = Math.atan2(hy, hx) - Math.PI / 2;
    //     let delta = target - flame.rotation;
    //     delta = Math.atan2(Math.sin(delta), Math.cos(delta));
    //     flame.rotation += elapsed <= PIXI.Ticker.shared.deltaMS ? delta : delta * 0.35;
    //   }
    //   flame.alpha = (flameCfg.ALPHA ?? 1) * Math.min(1, (1 - t) / 0.15);
    // }
    // Drop a flame behind the ball every flameGap ms, pointing back along
    // the stretch it just flew.
    if (flameFrames?.length && !container.destroyed) {
      sinceFlame += PIXI.Ticker.shared.deltaMS;
      const hx = pos.x - wrapper.x;
      const hy = pos.y - wrapper.y;
      if (sinceFlame >= flameGap && hx * hx + hy * hy > 0.01) {
        sinceFlame = 0;
        dropFlame(pos.x, pos.y, Math.atan2(hy, hx));
      }
    }
    wrapper.position.set(pos.x, pos.y);
    onProgress?.(progress);
    if (t >= 1) finish(true);
  };
  PIXI.Ticker.shared.add(tick);
};
